<template>
  <div
    v-if="showAuthenticatedLayout"
    id="upLayout"
    class="upLayout container-common"
  >
    <div v-show="auth0?.isAuthenticated" class="flex flex-row">
      <UpNavigationSidebarContainer />
      <div class="flex flex-col w-full overflow-hidden bg-white min-h-screen">
        <UpNavigationNavbarContainer v-if="isNavbarVisible" />
        <div :class="['flex-grow', isNavbarVisible && 'mx-6 my-4']">
          <slot />
        </div>
        <DefaultFooter class="mt-auto" />
      </div>
    </div>
  </div>
  <div v-else class="container-common flex flex-col min-h-screen">
    <div class="flex-grow">
      <slot />
    </div>
    <DefaultFooter class="py-4 flex-shrink-0 mt-auto" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useAuth0 } from "@auth0/auth0-vue";
import { useRoute } from "#vue-router";
import { IsUnauthenticatedRoute } from "~/utils/IsUnauthenticatedRoute";
import { useSearchStore } from "~/store/search";
import UpNavigationSidebarContainer from "~/components/sidebar/UpNavigationSidebar/UpNavigationSidebarContainer.vue";
import UpNavigationNavbarContainer from "~/components/header/UpNavigationNavbar/UpNavigationNavbarContainer.vue";
import DefaultFooter from "~/components/footer/DefaultFooter.vue";
import { useUpNavigationStore } from "~/store/upNavigationStore.ts";
import { useNavigationStore } from "@/store/navigation";
import type Emitter from "~/types/Emitter";

const auth0 = useAuth0();
const $route = useRoute();
const savedSearchStore = useSearchStore();
const navigationStore = useNavigationStore();
const upNavigationStore = useUpNavigationStore();
const { $bus } = useNuxtApp() as unknown as { $bus: Emitter };

const { isNavbarVisible } = storeToRefs(upNavigationStore);

const route = computed(() => {
  return $route.name as string;
});

const showAuthenticatedLayout = computed(() => {
  return process.server
    ? !IsUnauthenticatedRoute($route?.path ?? "")
    : auth0?.isAuthenticated?.value &&
        !IsUnauthenticatedRoute($route?.path ?? "");
});

onBeforeMount(() => {
  if (process.client) {
    const { locale } = useI18n();
    let userLanguage = localStorage.getItem("language");
    if (userLanguage === "null") userLanguage = null;

    locale.value = (userLanguage as typeof locale.value) ?? "en-us";
  }
});

onMounted(() => {
  savedSearchStore.currentRoute = route.value;
});

watch(
  () => route.value,
  (newVal) => {
    savedSearchStore.currentRoute = newVal;
  },
);

$bus.$on("leaveToManage", ({ path, menuItemUrl }) => {
  navigationStore.setActiveNavBarGroup(path);
  navigateTo(menuItemUrl, { external: false });
});
</script>

<style>
@import url("https://fonts.googleapis.com/css?family=Inter:300,400,700&display=swap");

.container-common {
  @apply tracking-[.02em] text-accent-800 bg-white font-sans min-h-screen;
}
</style>
